import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { observeAuthState } from '../firebase/auth.observer';
import { getUserProfile, backfillOnboardingCompleted, handleGoogleSignIn } from '../firebase/user.service';
import { initializeRealtimeSubscriptions, teardownRealtimeSubscriptions } from '../store/realtimeSubscriptions';
import useAppStore from '../store/appStore';
import { resolveStoredPhotoUrl } from '../utils/avatar';
import { createNormalizedUser, getUserFirstName } from '../utils/userName';

// Create the Auth Context
const AuthContext = createContext(null);

// Auth Context Provider
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // Firestore profile — { fullName, photoURL, role, ... }
    const [userProfile, setUserProfile] = useState(null);
    const [userProfileLoading, setUserProfileLoading] = useState(false);
    const [profileHydrated, setProfileHydrated] = useState(false);
    const authRequestIdRef = useRef(0);
    const normalizedUser = useMemo(() => createNormalizedUser(user, userProfile), [user, userProfile]);

    useEffect(() => {
        // Listen to Firebase auth state changes
        const unsubscribe = observeAuthState(async (authUser) => {
            const requestId = ++authRequestIdRef.current;
            setUser(authUser);
            setLoading(false);

            if (authUser) {
                setProfileHydrated(false);
                const isGoogleUser = authUser.providerData?.some((provider) => provider.providerId === 'google.com');

                if (isGoogleUser) {
                    try {
                        // Keep Firestore photoURL in sync with Google profile, including on reload.
                        await handleGoogleSignIn(authUser);
                    } catch (syncErr) {
                        console.warn('Google profile sync skipped:', syncErr);
                    }
                }

                // Fetch Firestore profile whenever auth user changes
                setUserProfileLoading(true);
                try {
                    const profile = await getUserProfile(authUser.uid);
                    if (requestId !== authRequestIdRef.current) return;

                    if (profile) {
                        const mergedProfile = {
                            ...profile,
                            photoURL: resolveStoredPhotoUrl(profile, authUser),
                            firstName: profile.firstName || getUserFirstName(profile),
                        };

                        // Backfill onboardingCompleted for legacy users
                        if (mergedProfile.onboardingCompleted !== true) {
                            await backfillOnboardingCompleted(authUser.uid);
                            if (requestId !== authRequestIdRef.current) return;
                            mergedProfile.onboardingCompleted = true;
                        }
                        setUserProfile(mergedProfile);

                        // Push profile into Zustand so all components can read it
                        const store = useAppStore.getState();
                        store.setUserProfile(mergedProfile);
                        store.setUser(authUser);
                        store.setSettingsProfile(mergedProfile);
                        if (mergedProfile.emailSignature) {
                            store.setSettingsEmailSignature(mergedProfile.emailSignature);
                        }
                    } else {
                        // No Firestore document — do not fabricate a stub profile
                        setUserProfile(null);
                    }
                } catch (err) {
                    if (requestId !== authRequestIdRef.current) return;
                    console.error('Failed to fetch Firestore user profile:', err);
                    setUserProfile(null);
                } finally {
                    if (requestId !== authRequestIdRef.current) return;
                    setUserProfileLoading(false);
                    setProfileHydrated(true);
                }

                // Initialize all realtime Firestore listeners
                initializeRealtimeSubscriptions(authUser.uid);
            } else {
                setUserProfile(null);
                setUserProfileLoading(false);
                setProfileHydrated(true);
                // Tear down all listeners and reset store on logout
                teardownRealtimeSubscriptions();
            }
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, []);

    // Allow other components (e.g. Settings) to push an updated profile into context
    // without requiring a page reload.
    const refreshUserProfile = async () => {
        if (!user) return;
        setUserProfileLoading(true);
        try {
            const profile = await getUserProfile(user.uid);
            if (profile) {
                const mergedProfile = {
                    ...profile,
                    photoURL: resolveStoredPhotoUrl(profile, user),
                    firstName: profile.firstName || getUserFirstName(profile),
                };
                setUserProfile(mergedProfile);
                // Also update Zustand
                const store = useAppStore.getState();
                store.setUserProfile(mergedProfile);
                store.setSettingsProfile(mergedProfile);
                if (mergedProfile.emailSignature) {
                    store.setSettingsEmailSignature(mergedProfile.emailSignature);
                }
            }
        } catch (err) {
            console.error('Failed to refresh Firestore user profile:', err);
        } finally {
            setUserProfileLoading(false);
        }
    };

    return (
        <AuthContext.Provider value={{ user, normalizedUser, loading, userProfile, userProfileLoading, profileHydrated, refreshUserProfile }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
